import Big from 'big.js';

import { Currencies } from '../../common/constants';
import { CompanyProductSubscription } from '../../common/interfaces/company-product-subscription';
import {
  EmployerPurchaseProductType,
  Job,
  SubscriptionPlanType,
} from '../../common/interfaces/entities';
import {
  HostedPaymentPageServices,
  PaymentService as GlobalPaymentService,
} from '../../common/interfaces/payment-service';
import { CompanyProductTrialInformationDetail } from '../../common/interfaces/product-free-trial-offer';
import { TimePeriodUnit } from '../../common/interfaces/time-period-unit';
import type { ProductBenefit } from '../PaymentOrder/types';

export const CreditExpiryWindow = {
  WITHIN_7_DAYS: 'WITHIN_7_DAYS',
  WITHIN_30_DAYS: 'WITHIN_30_DAYS',
  OTHER: 'OTHER',
} as const;

export type CreditExpiryWindow =
  (typeof CreditExpiryWindow)[keyof typeof CreditExpiryWindow];

export type CompanyCreditInfo = {
  totalAmount: number;
  nextExpiringCredit: {
    amount: number;
    expiryDateTime: string;
    expiryWindow: CreditExpiryWindow;

    // client only field
    canShowExpiringNotification: boolean;
  } | null;
};

// TODO: Afzal replace with global PaymentService
export const PaymentService = GlobalPaymentService;
export type PaymentService = GlobalPaymentService;

export const CreditPurchaseOrderStatus = {
  PENDING: 'PENDING',
  COMPLETE: 'COMPLETE',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;

export type CreditPurchaseOrderStatus =
  (typeof CreditPurchaseOrderStatus)[keyof typeof CreditPurchaseOrderStatus];

export const CreditProductPurchaseOrderStatus = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;

export type CreditProductPurchaseOrderStatus =
  (typeof CreditProductPurchaseOrderStatus)[keyof typeof CreditProductPurchaseOrderStatus];

export type CreditPurchaseOrderQRPayment = {
  paymentService: typeof PaymentService.XENDIT_QR_CODE;
  qrCodeString: string;
  paymentUrl: null;
};

export type CreditBenefit = {
  amount: number;
  frequency?: string;
};

export type ActiveFreeCreditBenefit = {
  amount: number;
  nextAllocationDate: string | null;
};

export type CreditPurchaseOrderInvoicePayment = {
  paymentService: HostedPaymentPageServices;
  paymentUrl: string;
  qrCodeString: null;
};

export type CreditPurchaseOrderXenditSubscriptionPayment = {
  paymentService: typeof PaymentService.XENDIT_SUBSCRIPTION;
  paymentUrl: string;
  qrCodeString: null;
};

export const XenditSubscriptionPurchaseFailureCode = {
  INVALID_OTP: 'INVALID_OTP',
  EXPIRED_OTP: 'EXPIRED_OTP',
  USER_DECLINED_PAYMENT: 'USER_DECLINED_PAYMENT',
  USER_DID_NOT_AUTHORIZE: 'USER_DID_NOT_AUTHORIZE',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  USER_DEVICE_UNREACHABLE: 'USER_DEVICE_UNREACHABLE',
  OTP_ATTEMPT_COUNTS_EXCEEDED: 'OTP_ATTEMPT_COUNTS_EXCEEDED',
  CARD_EXPIRED: 'CARD_EXPIRED',
  CARD_DECLINED: 'CARD_DECLINED',
  DECLINED_BY_ISSUER: 'DECLINED_BY_ISSUER',
  DECLINED_BY_PROCESSOR: 'DECLINED_BY_PROCESSOR',
  ISSUER_UNAVAILABLE: 'ISSUER_UNAVAILABLE',
  INVALID_ACCOUNT_DETAILS: 'INVALID_ACCOUNT_DETAILS',
  ACCOUNT_NOT_ACTIVATED: 'ACCOUNT_NOT_ACTIVATED',
  ACCOUNT_ACCESS_BLOCKED: 'ACCOUNT_ACCESS_BLOCKED',
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
  PAYMENT_AMOUNT_LIMITS_EXCEEDED: 'PAYMENT_AMOUNT_LIMITS_EXCEEDED',
  PAYMENT_ATTEMPT_COUNTS_EXCEEDED: 'PAYMENT_ATTEMPT_COUNTS_EXCEEDED',
  INACTIVE_PAYMENT_REQUEST: 'INACTIVE_PAYMENT_REQUEST',
  PAYMENT_REQUEST_EXPIRED: 'PAYMENT_REQUEST_EXPIRED',
  INELIGIBLE_TRANSACTION_STATUS: 'INELIGIBLE_TRANSACTION_STATUS',
  CAPTURE_AMOUNT_EXCEEDED: 'CAPTURE_AMOUNT_EXCEEDED',
  REFUND_AMOUNT_EXCEEDED: 'REFUND_AMOUNT_EXCEEDED',
  INVALID_VALUE_ERROR: 'INVALID_VALUE_ERROR',
  API_VALIDATION_ERROR: 'API_VALIDATION_ERROR',
  INVALID_PAYMENT_DETAILS: 'INVALID_PAYMENT_DETAILS',
  INVALID_TOKEN: 'INVALID_TOKEN',
  BLOCKED_TOKEN: 'BLOCKED_TOKEN',
  DATA_NOT_FOUND: 'DATA_NOT_FOUND',
  DUPLICATE_ERROR: 'DUPLICATE_ERROR',
  UNSUPPORTED_CONTENT_TYPE: 'UNSUPPORTED_CONTENT_TYPE',
  TEMPORARILY_UNAVAILABLE: 'TEMPORARILY_UNAVAILABLE',
  CHANNEL_UNAVAILABLE: 'CHANNEL_UNAVAILABLE',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  FAILURE_DETAILS_UNAVAILABLE: 'FAILURE_DETAILS_UNAVAILABLE',
  PAYMENT_REQUEST_RATE_LIMITED: 'PAYMENT_REQUEST_RATE_LIMITED',
  ISSUER_SUSPECT_FRAUD: 'ISSUER_SUSPECT_FRAUD',
  STOLEN_CARD: 'STOLEN_CARD',
  INACTIVE_OR_UNAUTHORIZED_CARD: 'INACTIVE_OR_UNAUTHORIZED_CARD',
  PROCESSOR_ERROR: 'PROCESSOR_ERROR',
  INVALID_CVV: 'INVALID_CVV',
} as const;

export type XenditSubscriptionPurchaseFailureCode =
  (typeof XenditSubscriptionPurchaseFailureCode)[keyof typeof XenditSubscriptionPurchaseFailureCode];

export type XenditSubscriptionPaymentRequestInfo = {
  paymentService: typeof PaymentService.XENDIT_SUBSCRIPTION;
  failureCode: XenditSubscriptionPurchaseFailureCode | null;
};

export const PurchaseOrderPurchaseType = {
  ONE_TIME: 'ONE_TIME',
  SUBSCRIPTION: 'SUBSCRIPTION',
} as const;

export type PurchaseOrderPurchaseType =
  (typeof PurchaseOrderPurchaseType)[keyof typeof PurchaseOrderPurchaseType];

export type CreditPurchaseOrder = {
  id: string;
  status: CreditPurchaseOrderStatus;
  redirectPathAfterPayment: string;
  totalCredits: number;
  createdAt: string;
  totalPayment: Big;
  currency: string;
  paymentExpiryAt: string;
  purchaseType: PurchaseOrderPurchaseType;
  activeSubscriptionInformation: CompanyProductSubscription | null;
  paymentRequestInfo?: XenditSubscriptionPaymentRequestInfo | null;
} & (
  | CreditPurchaseOrderQRPayment
  | CreditPurchaseOrderInvoicePayment
  | CreditPurchaseOrderXenditSubscriptionPayment
);

export const CreditProductPackageDiscountTag = {
  THIRTY_THREE_OFF: 'THIRTY_THREE_OFF',
  TWENTY_FIVE_OFF: 'TWENTY_FIVE_OFF',
  TWENTY_OFF: 'TWENTY_OFF',
} as const;

export const CreditProductPackageTag = {
  ...CreditProductPackageDiscountTag,
  POPULAR: 'POPULAR',
} as const;
export type CreditProductPackageTag =
  (typeof CreditProductPackageTag)[keyof typeof CreditProductPackageTag];

export const creditProductPackageTagToDiscountPercentage: Record<
  Exclude<CreditProductPackageTag, 'POPULAR'>,
  number
> = {
  [CreditProductPackageTag.THIRTY_THREE_OFF]: 33,
  [CreditProductPackageTag.TWENTY_FIVE_OFF]: 25,
  [CreditProductPackageTag.TWENTY_OFF]: 20,
} as const;

export type ProductPackageCustomisationPricingInfo = {
  additionalJobSlot: {
    value: number;
    currency: string;
    totalCredits: number;
    amount: Big;
    pricingInfo: PricingInfo;
  }[];
};

export type PricingInfo = {
  originalPrice: Big;
  savedAmount: Big;
  totalAmount: Big;
  pricePerTimePeriodUnit: Big;
  currency: Currencies;
};

export type CreditProductPackage = {
  id: string;
  productId: string;
  productQuantity: number;
  product: {
    type: EmployerPurchaseProductType;
    creditBenefit: CreditBenefit | null;
    productBenefits?: ProductBenefit[] | null;
  };
  creditQuantity: number;
  creditQuantityAfterDiscount: number;
  creditDiscountPercentage: number | null;
  timePeriod: number | null;
  timePeriodUnit: TimePeriodUnit | null;
  expiresAt: string | null;
  pricingInfo: PricingInfo;
  tag: CreditProductPackageTag | null;
  customisationPricing: ProductPackageCustomisationPricingInfo | null;
  boostPeriod?: number | null;
  boostPeriodUnit?: string | null;
};

export type CreditProductSubscriptionPackage = {
  id: string;
  product: {
    creditBenefit: CreditBenefit | null;
    productBenefits?: ProductBenefit[] | null;
  };
  totalPrice: Big;
  currency: Currencies;
  productId: string;
  productTimePeriod: number;
  productTimePeriodUnit: TimePeriodUnit;
  paymentFrequencyTimePeriod: number;
  paymentFrequencyTimePeriodUnit: TimePeriodUnit;
  activeFreeTrialOffer: {
    id: string;
    trialTimePeriod: number;
    trialTimePeriodUnit: TimePeriodUnit;
  } | null;
  customisationPricing: ProductPackageCustomisationPricingInfo | null;
  pricingInfo: PricingInfo;
  tag: CreditProductPackageTag | null;
};

type AfterPurchaseActionLog = {
  relatedJobId: string | null;
  job: Required<Pick<Job, 'id' | 'jobBoost'>> | null;
};

export type HotJobProductVariantDetail = {
  durationCount: number;
  durationUnit: string;
};

export type ProductVariantDetail = HotJobProductVariantDetail;

const PurchaseDetailsPurchaseType = {
  PURCHASED: 'PURCHASED',
  EXTENDED: 'EXTENDED',
  PENDING: 'PENDING',
} as const;

export const VIPMembershipPurchaseType = PurchaseDetailsPurchaseType;
export type VIPMembershipPurchaseType =
  (typeof VIPMembershipPurchaseType)[keyof typeof VIPMembershipPurchaseType];

type VIPMembershipPurchaseDetails = {
  purchaseType: VIPMembershipPurchaseType;
};

export const PremiumPlanPurchaseDetailsType = PurchaseDetailsPurchaseType;
export type PremiumPlanPurchaseDetailsType =
  (typeof PremiumPlanPurchaseDetailsType)[keyof typeof PremiumPlanPurchaseDetailsType];

type PremiumPlanPurchaseDetails = {
  purchaseType: PremiumPlanPurchaseDetailsType;
};
export type CreditProductPurchaseOrderHotJobProduct = {
  type: typeof EmployerPurchaseProductType.HOT_JOB;
  variantDetail: ProductVariantDetail;
};

export type CreditProductPurchaseOrderPremiumPlanProduct = {
  type: typeof EmployerPurchaseProductType.PREMIUM_PLAN;
  variantDetail: {
    planType: SubscriptionPlanType;
  };
};

type CreditProductPurchaseOrderNonHotJobProduct = {
  type: Exclude<
    EmployerPurchaseProductType,
    | typeof EmployerPurchaseProductType.HOT_JOB
    | typeof EmployerPurchaseProductType.PREMIUM_PLAN
  >;
  variantDetail: null;
};

type CreditProductPurchaseOrderProduct =
  | CreditProductPurchaseOrderHotJobProduct
  | CreditProductPurchaseOrderPremiumPlanProduct
  | CreditProductPurchaseOrderNonHotJobProduct;

export type CreditProductPurchaseOrder = {
  id: string;
  status: CreditProductPurchaseOrderStatus;
  productQuantity: number;
  productTimePeriod: number | null;
  productTimePeriodUnit: string | null;
  creditPurchaseOrder: CreditPurchaseOrder | null;
  companyProductInventory: {
    amount: number;
    expiryDateTime: string;
    trialInformationDetail: CompanyProductTrialInformationDetail;
  } | null;
  product: CreditProductPurchaseOrderProduct;
  afterPurchaseActionLog: AfterPurchaseActionLog | null;
  purchaseProductSpecificDetails:
    | VIPMembershipPurchaseDetails
    | PremiumPlanPurchaseDetails
    | null;
  creditBenefit: CreditBenefit | null;
  purchaseType: PurchaseOrderPurchaseType;
  activeSubscriptionInformation: CompanyProductSubscription | null;
};
